import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Card, CardContent, Spinner, Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui'
import { Button } from '@/components/ui'
import { Badge, Tip } from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { ChatContainer, SourcePanel } from '@/components/chat'
import { ConversationsExportControl } from '@/components/conversations/ConversationsExportControl'
import { RagStatusBadge } from '@/components/chat/RagStatusBadge'
import { KeyStatusLine, VoiceStatusLine } from '@/components/chat/VoiceStatusLine'
import { VOICE_UNAVAILABLE_VIEW_AS } from '@/components/chat/voiceStatusText'
import { KEYS_UNAVAILABLE } from '@/components/auth/impersonationText'
import { useAuthStore } from '@/stores/authStore'
import { ConversationList } from '@/components/conversations/ConversationList'
import { TavusAvatarDisplay, AvatarControls, ReplicaSelectorModal } from '@/components/avatar'
import { usePersonaStore, useSettingsStore, useChatStore } from '@/stores'
import { useAvatar } from '@/hooks/useAvatar'
import { useAvatarRAG } from '@/hooks/useAvatarRAG'
import { useRagProxyBase } from '@/hooks/useRagProxyBase'
import { useConversations, useResizablePanel, useFeature } from '@/hooks'
import { useIsWide } from '@/hooks/useBreakpoint'
import { transformRAGSources } from '@/utils/sourceUtils'
import { cn } from '@/utils'
import {
  Settings2,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  Video,
  MessageSquare,
  RefreshCw,
  Mic,
  BookOpen,
  GripVertical,
} from 'lucide-react'

type ViewMode = 'chat' | 'avatar'

export function ConversationsPage() {
  const { conversationId } = useParams<{ conversationId?: string }>()
  const navigate = useNavigate()
  // Changes on every navigation, a push to the same address included.
  const { key: navigationKey } = useLocation()

  const { personas } = usePersonaStore()
  const { getTavusKey, hasTavusKey, hasAnamKey, hasElevenLabsKey } = useSettingsStore()
  const viewingAs = useAuthStore((s) => s.impersonation !== null)
  const showVoice = useFeature('voice')
  const showAvatars = useFeature('avatars')
  const { conversations } = useChatStore()

  const {
    currentConversationId,
    isLoading: isConversationLoading,
    selectConversation,
  } = useConversations()

  // Get persona from the current conversation
  const currentConversation = currentConversationId ? conversations[currentConversationId] : null
  const conversationPersonaId = currentConversation?.personaId
  const activePersona = conversationPersonaId ? personas[conversationPersonaId] : null

  // View mode - simple chat or avatar
  const [viewMode, setViewMode] = useState<ViewMode>('chat')
  const [showConfig, setShowConfig] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showReplicaSelector, setShowReplicaSelector] = useState(false)

  // The recent list (SA-961, spec §2.2–§2.3): a column at xl, a drawer below.
  // Not lg: between the two the column left the chat about 430px wide.
  // One of the two is mounted, never both; widening the window closes the drawer.
  const isWide = useIsWide()
  const [listOpen, setListOpen] = useState(false)
  if (isWide && listOpen) setListOpen(false)

  // Avatar integration (only used when in avatar mode)
  // Memoize options to prevent useAvatar callbacks from being recreated every render
  const avatarOptions = useMemo(() => ({
    onError: (error: Error) => console.error('Avatar error:', error),
  }), [])

  const {
    isInitialized: avatarInitialized,
    avatars,
    sessionState: avatarState,
    videoElement,
    isVideoReady,
    agentMessage: avatarAgentMessage,
    initialize: initAvatar,
    startSession: startAvatarSession,
    stopSession: stopAvatarSession,
    interrupt: interruptAvatar,
    getSession,
  } = useAvatar(avatarOptions)

  // Expert RAG mode for avatar
  const isExpertRAG = activePersona?.avatarConfig?.pipelineMode === 'expert_rag'
  const { connections } = useSettingsStore()
  const ragProxyBase = useRagProxyBase()

  const {
    ragState,
    messages: ragMessages,
    currentSources,
    audioLevel,
    isListening,
    isProcessing,
    startRAG,
    stopRAG,
    interruptRAG,
  } = useAvatarRAG({
    onError: (error) => console.error('Avatar RAG error:', error),
  })

  // Resizable split for Expert RAG layout
  const {
    size: avatarPanelWidth,
    isDragging: isResizing,
    handleMouseDown: handleResizeMouseDown,
  } = useResizablePanel({
    defaultSize: 500,
    minSize: 300,
    maxSize: 900,
    storageKey: 'avatar-rag-split-width',
    side: 'left',
  })

  // Transform RAG sources for the SourcePanel
  const displaySources = useMemo(
    () => transformRAGSources(currentSources),
    [currentSources]
  )

  // Auto-scroll ref for RAG chat messages
  const ragChatRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (ragChatRef.current) {
      ragChatRef.current.scrollTop = ragChatRef.current.scrollHeight
    }
  }, [ragMessages])

  // The address's conversation is asked for once per address (SA-974, SA-975).
  // - `address`: the address's id as of the last commit.
  // - `asked`: the id this address was asked for.
  // - `failed`: the id this address could not open (not a 404).
  // - `gone`: the id this address found gone (a 404).
  // These reset only when the address really changes. So StrictMode's second
  // run asks nothing twice, and a failure is not retried until the next
  // navigation. Clicking the row in the list is a deliberate retry: it calls
  // selectConversation itself.
  const address = useRef(conversationId)
  const asked = useRef<string | null>(null)
  const failed = useRef<string | null>(null)
  const gone = useRef<string | null>(null)
  useEffect(() => {
    if (address.current === conversationId) return
    address.current = conversationId
    asked.current = null
    failed.current = null
    gone.current = null
  }, [conversationId])

  // Load conversation from URL if specified
  useEffect(() => {
    if (!conversationId || conversationId === currentConversationId) return
    // A gone id is let go on every navigation to it. The list pushes a picked
    // row's address after its 404, and when that push lands with this page's
    // own replace in one render, the address never visibly changes.
    if (gone.current === conversationId) {
      navigate('/conversations', { replace: true })
      return
    }
    if (asked.current === conversationId) return
    asked.current = conversationId
    const id = conversationId
    void (async () => {
      const outcome = await selectConversation(id)
      // The address moved on meanwhile: the answer is not this address's.
      if (address.current !== id) return
      // Gone: the address lets it go, replacing it so Back does not return.
      if (outcome === 'gone') {
        gone.current = id
        navigate('/conversations', { replace: true })
      } else if (outcome === 'failed') failed.current = id
    })()
  }, [conversationId, currentConversationId, selectConversation, navigate, navigationKey])

  // Update URL when conversation changes. It follows the selection when it
  // holds no id, or the id that failed here: a conversation created there
  // (ChatContainer, nothing selected) takes the address (SA-975). An address
  // that opened is never rewritten here: a pick from the list selects first,
  // then navigates.
  useEffect(() => {
    if (!currentConversationId || currentConversationId === conversationId) return
    if (!conversationId || failed.current === conversationId) {
      navigate(`/conversations/${currentConversationId}`, { replace: true })
    }
  }, [currentConversationId, conversationId, navigate])

  // Determine which avatar provider the active persona needs
  const avatarProviderType = activePersona?.avatarConfig?.provider || 'tavus'
  const hasAvatarKey = avatarProviderType === 'anam' ? hasAnamKey : hasTavusKey

  // Initialize avatar provider when switching to avatar mode
  useEffect(() => {
    const initializeAvatar = async () => {
      if (viewMode === 'avatar' && !avatarInitialized && hasAvatarKey) {
        try {
          if (avatarProviderType === 'anam') {
            // Anam key is server-side, pass empty string
            await initAvatar('', 'anam')
          } else {
            const tavusKey = getTavusKey()
            if (tavusKey) {
              await initAvatar(tavusKey, 'tavus')
            }
          }
          console.log(`[ConversationsPage] ${avatarProviderType} avatar provider initialized`)
        } catch (error) {
          console.warn(`[ConversationsPage] Failed to initialize ${avatarProviderType} avatar:`, error)
        }
      }
    }

    initializeAvatar()
  }, [viewMode, avatarInitialized, hasAvatarKey, avatarProviderType, getTavusKey, initAvatar])

  // Handle avatar session
  const handleStartAvatar = useCallback(async () => {
    if (!activePersona?.avatarConfig) return

    const config = activePersona.avatarConfig
    const isAnam = config.provider === 'anam'

    // Validate required fields
    if (!isAnam && !config.replicaId) return
    if (isAnam && !config.anamPersonaId && !config.anamAvatarId) return

    const defaultMode = isAnam ? 'anam_ai' : 'tavus_ai'
    const pipelineMode = config.pipelineMode || defaultMode

    try {
      await startAvatarSession({
        // Tavus-specific
        replicaId: config.replicaId,
        personaId: config.personaId,
        // Anam-specific
        anamPersonaId: config.anamPersonaId,
        anamAvatarId: config.anamAvatarId,
        anamVoiceId: config.anamVoiceId,
        anamLlmId: config.anamLlmId,
        anamSystemPrompt: config.anamSystemPrompt,
        anamEnableRecording: config.anamEnableRecording,
        anamEnableClientTools: config.anamEnableClientTools,
        anamLanguageCode: config.anamLanguageCode,
        // Shared
        pipelineMode,
      })

      // In Expert RAG mode, start the RAG engine after the avatar session is ready
      if (pipelineMode === 'expert_rag') {
        // Wait briefly for the session to stabilize
        await new Promise((resolve) => setTimeout(resolve, 500))
        const session = getSession()
        if (session) {
          const ragServerUrl = activePersona.ragConfig.serverUrl || ragProxyBase
          const ragApiKey =
            activePersona.ragConfig.apiKey || connections.ragApiKey || ''

          await startRAG(session, activePersona.ragConfig, ragServerUrl, ragApiKey, undefined, activePersona.voiceConfig?.speechMode)
        }
      }
    } catch (error) {
      console.error('Failed to start avatar session:', error)
    }
  }, [activePersona, startAvatarSession, getSession, startRAG, connections])

  const handleStopAvatar = useCallback(async () => {
    if (isExpertRAG) {
      await stopRAG()
    }
    await stopAvatarSession()
  }, [isExpertRAG, stopRAG, stopAvatarSession])

  const handleInterruptAvatar = useCallback(() => {
    if (isExpertRAG) {
      interruptRAG()
    }
    interruptAvatar()
  }, [isExpertRAG, interruptRAG, interruptAvatar])

  return (
    <PageFrame width="list" className="flex h-full flex-col gap-4">
      <PageHeader title="Conversations" />
      <div className="flex min-h-0 flex-1 gap-4">
      {isWide && (
        <aside aria-label="Conversations" className="flex w-64 shrink-0 flex-col">
          <Card className="min-h-0 flex-1 gap-0 overflow-hidden py-0">
            <ConversationList />
          </Card>
        </aside>
      )}

      <div className="flex flex-col gap-4 h-full min-w-0 flex-1">
        {/* Header with mode toggle and status */}
        <div>
          <Card className="gap-0">
            {/* Mode toggle and persona info. Wraps rather than overflowing on a
                phone, where the Conversations button joins it. */}
            <div className="flex flex-wrap items-center justify-between gap-2 p-3">
              <div className="flex flex-wrap items-center gap-3">
                {!isWide && (
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<MessageSquare className="w-4 h-4" />}
                    aria-haspopup="dialog"
                    aria-expanded={listOpen}
                    onClick={() => setListOpen(true)}
                  >
                    Conversations
                  </Button>
                )}

                {/* Mode toggle buttons */}
                <div className="flex bg-muted rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('chat')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'chat'
                        ? 'bg-card text-foreground shadow-xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Chat
                  </button>
                  {showAvatars && (
                    <button
                      onClick={() => setViewMode('avatar')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'avatar'
                          ? 'bg-card text-foreground shadow-xs'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Video className="w-4 h-4" />
                      Avatar
                    </button>
                  )}
                </div>

                {/* Persona info */}
                <button
                  onClick={() => setShowConfig(!showConfig)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-muted transition-colors"
                >
                  <Settings2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {activePersona ? activePersona.name : 'Default'}
                  </span>
                  {showConfig ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>
              </div>

              {/* Status badges */}
              <div className="flex items-center gap-2">
                <ConversationsExportControl />
                <RagStatusBadge />
                {/* SA-1418 (B5): under a view-as the viewer's keys are never read, so the badge says why. */}
                {showVoice && viewMode === 'chat' && (viewingAs ? (
                  <Tip text={KEYS_UNAVAILABLE}><Badge variant="warning-soft" tabIndex={0}>{VOICE_UNAVAILABLE_VIEW_AS}</Badge></Tip>
                ) : hasElevenLabsKey && (
                  <Badge variant="success">Voice Ready</Badge>
                ))}
                {showAvatars && viewMode === 'avatar' && (
                  <>
                    <Badge variant={avatarInitialized ? 'success' : 'secondary'}>
                      Avatar: {avatarInitialized ? 'Ready' : 'Not configured'}
                    </Badge>
                    {isExpertRAG && (
                      <Badge variant="secondary">
                        Expert RAG
                      </Badge>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Expanded persona config */}
            {showConfig && (
              <CardContent className="border-t border-border pt-3">
                {activePersona ? (
                  <div className="flex items-start gap-4">
                    {activePersona.avatarUrl ? (
                      <img
                        src={activePersona.avatarUrl}
                        alt={activePersona.name}
                        className="w-16 h-16 rounded-lg object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-linear-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground font-bold text-2xl shrink-0">
                        {activePersona.name.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 space-y-2">
                      <div>
                        <h3 className="font-medium text-foreground">
                          {activePersona.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {activePersona.description || 'This persona is active for this conversation.'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge>Mode: {activePersona.presentationMode}</Badge>
                        {showVoice && activePersona.voiceConfig && (
                          <Badge variant="secondary">
                            Voice: {activePersona.voiceConfig.provider}
                          </Badge>
                        )}
                        {showAvatars && activePersona.avatarConfig && (
                          <Badge variant="secondary">
                            Avatar: {activePersona.avatarConfig.provider}
                          </Badge>
                        )}
                        {activePersona.ragConfig.model && (
                          <Badge variant="secondary">
                            Model: {activePersona.ragConfig.model}
                          </Badge>
                        )}
                      </div>

                      {/* API key status */}
                      <div className="flex items-center gap-4 text-sm">
                        {showVoice && (
                          <VoiceStatusLine />
                        )}
                        {showAvatars && (
                          <KeyStatusLine service="avatar" hasKey={hasAvatarKey} />
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      No persona selected - using default settings. Select a persona from the Personas page to customize the AI behavior.
                    </p>
                    {/* API key status */}
                    <div className="flex items-center gap-4 text-sm">
                      {showVoice && (
                        <VoiceStatusLine />
                      )}
                      {showAvatars && (
                        <KeyStatusLine service="avatar" hasKey={hasAvatarKey} />
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>

        {/* Main content area */}
        <div className="flex-1 min-h-0">
          <Card className="h-full overflow-hidden">
            {isConversationLoading ? (
              <div className="h-full flex items-center justify-center">
                <Spinner className="w-8 h-8" />
              </div>
            ) : viewMode === 'chat' ? (
              // Chat mode with integrated voice controls
              <ChatContainer className="h-full" showVoiceControls={true} />
            ) : (
              // Avatar mode
              <div className="h-full flex flex-col">
                {hasAvatarKey ? (
                  <>
                    {isExpertRAG ? (
                      <>
                        {/* Expert RAG: Split layout — avatar + chat transcript */}
                        <div className={cn('flex-1 flex min-h-0', isResizing && 'select-none')}>
                          {/* Left: Avatar video */}
                          <div
                            className="shrink-0 flex items-center justify-center p-4 bg-muted"
                            style={{ width: avatarPanelWidth }}
                          >
                            <TavusAvatarDisplay
                              videoElement={videoElement}
                              isReady={isVideoReady}
                              sessionState={avatarState}
                              size="large"
                              onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
                              isFullscreen={isFullscreen}
                              className="w-full max-w-none"
                            />
                          </div>

                          {/* Resize handle */}
                          <div
                            onMouseDown={handleResizeMouseDown}
                            className={cn(
                              'shrink-0 w-1.5 cursor-col-resize group flex items-center justify-center',
                              'bg-border hover:bg-primary/50 transition-colors',
                              isResizing && 'bg-primary'
                            )}
                          >
                            <GripVertical
                              className={cn(
                                'w-3 h-6 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity',
                                isResizing && 'opacity-100 text-primary-foreground'
                              )}
                            />
                          </div>

                          {/* Right: Chat transcript + sources */}
                          <div className="flex-1 min-w-0 flex flex-col min-h-0">
                            {/* RAG state indicator bar */}
                            <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-background">
                              <Badge
                                variant={
                                  ragState === 'listening' ? 'success' :
                                  ragState === 'processing' ? 'warning' :
                                  'secondary'
                                }
                              >
                                {ragState === 'listening' ? 'Listening' :
                                 ragState === 'processing' ? 'Processing...' :
                                 ragState === 'idle' ? 'Idle' : ragState}
                              </Badge>
                              {isListening && (
                                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                  <Mic className="w-3.5 h-3.5 text-success" />
                                  <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-success rounded-full transition-all duration-100"
                                      style={{ width: `${Math.min(audioLevel * 100, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                              {isProcessing && (
                                <div className="flex items-center gap-1 text-sm text-primary">
                                  <Spinner className="w-3.5 h-3.5" />
                                  <span>Thinking...</span>
                                </div>
                              )}
                            </div>

                            {/* Chat messages */}
                            <div ref={ragChatRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                              {ragMessages.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
                                  <BookOpen className="w-8 h-8 mb-2 opacity-50" />
                                  <p className="text-sm font-medium">Expert RAG Mode</p>
                                  <p className="text-xs mt-1">
                                    Speak to the avatar — responses come from your knowledge base
                                  </p>
                                </div>
                              ) : (
                                ragMessages.map((msg) => (
                                  <div
                                    key={msg.id}
                                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                  >
                                    <div
                                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                                        msg.role === 'user'
                                          ? 'bg-primary text-primary-foreground'
                                          : 'bg-muted text-foreground'
                                      }`}
                                    >
                                      <p className="whitespace-pre-wrap">{msg.content}</p>
                                      {msg.isStreaming && (
                                        <span className="inline-block w-1.5 h-4 ml-0.5 bg-current animate-pulse rounded-xs" />
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>

                            {/* Sources panel */}
                            {displaySources.length > 0 && (
                              <div className="border-t border-border max-h-48 overflow-y-auto">
                                <SourcePanel sources={displaySources} className="p-2" />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Expert RAG controls */}
                        <div className="p-4 border-t border-border bg-background">
                          <div className="max-w-md mx-auto space-y-4">
                            <AvatarControls
                              sessionState={avatarState}
                              onStartSession={handleStartAvatar}
                              onStopSession={handleStopAvatar}
                              onInterrupt={handleInterruptAvatar}
                              disabled={!avatarInitialized || !(activePersona?.avatarConfig?.replicaId || activePersona?.avatarConfig?.anamPersonaId || activePersona?.avatarConfig?.anamAvatarId)}
                              className="w-full"
                            />

                            {avatarInitialized && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowReplicaSelector(true)}
                                leftIcon={<Video className="w-4 h-4" />}
                                className="w-full"
                              >
                                Select Avatar
                              </Button>
                            )}

                            {!avatarInitialized && hasAvatarKey && (
                              <Button
                                variant="secondary"
                                size="sm"
                                leftIcon={<RefreshCw className="w-4 h-4" />}
                                onClick={async () => {
                                  try {
                                    if (avatarProviderType === 'anam') {
                                      await initAvatar('', 'anam')
                                    } else {
                                      const key = getTavusKey()
                                      if (key) await initAvatar(key, 'tavus')
                                    }
                                  } catch (error) {
                                    console.error('[ConversationsPage] Avatar init failed:', error)
                                  }
                                }}
                                className="w-full"
                              >
                                Initialize Avatar Provider
                              </Button>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Tavus AI mode: Original layout */}
                        <div className="flex-1 flex items-center justify-center p-4 bg-muted">
                          <TavusAvatarDisplay
                            videoElement={videoElement}
                            isReady={isVideoReady}
                            sessionState={avatarState}
                            size="large"
                            onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
                            isFullscreen={isFullscreen}
                            className="w-full max-w-2xl"
                          />
                        </div>

                        <div className="p-4 border-t border-border bg-background">
                          <div className="max-w-md mx-auto space-y-4">
                            <AvatarControls
                              sessionState={avatarState}
                              onStartSession={handleStartAvatar}
                              onStopSession={stopAvatarSession}
                              onInterrupt={interruptAvatar}
                              disabled={!avatarInitialized || !(activePersona?.avatarConfig?.replicaId || activePersona?.avatarConfig?.anamPersonaId || activePersona?.avatarConfig?.anamAvatarId)}
                              className="w-full"
                            />

                            {avatarAgentMessage && (
                              <div className="p-3 bg-primary/10 rounded-lg">
                                <p className="text-sm text-primary">
                                  {avatarAgentMessage}
                                </p>
                              </div>
                            )}

                            {avatarInitialized && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowReplicaSelector(true)}
                                leftIcon={<Video className="w-4 h-4" />}
                                className="w-full"
                              >
                                Select Avatar
                              </Button>
                            )}

                            {!avatarInitialized && hasAvatarKey && (
                              <Button
                                variant="secondary"
                                size="sm"
                                leftIcon={<RefreshCw className="w-4 h-4" />}
                                onClick={async () => {
                                  try {
                                    if (avatarProviderType === 'anam') {
                                      await initAvatar('', 'anam')
                                    } else {
                                      const key = getTavusKey()
                                      if (key) await initAvatar(key, 'tavus')
                                    }
                                  } catch (error) {
                                    console.error('[ConversationsPage] Avatar init failed:', error)
                                  }
                                }}
                                className="w-full"
                              >
                                Initialize Avatar Provider
                              </Button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  // No avatar API key configured
                  <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-warning/20 flex items-center justify-center mb-4">
                      <AlertCircle className="w-8 h-8 text-warning" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      Avatar Not Configured
                    </h3>
                    <p className="text-muted-foreground max-w-sm mb-4">
                      Add your {avatarProviderType === 'anam' ? 'Anam' : 'Tavus'} API key in Settings to enable video avatar conversations.
                    </p>
                    <Button
                      variant="default"
                      onClick={() => navigate('/settings')}
                    >
                      Go to Settings
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Replica Selector Modal */}
        <ReplicaSelectorModal
          open={showReplicaSelector}
          onOpenChange={setShowReplicaSelector}
          replicas={avatars}
          selectedReplicaId={activePersona?.avatarConfig?.replicaId}
          onSelect={(replica) => {
            console.log('Selected replica:', replica)
            // Would update persona config via backend
          }}
          isLoading={!avatarInitialized}
        />
      </div>

      <Drawer open={listOpen} onOpenChange={setListOpen}>
        <DrawerContent
          className="sm:max-w-sm"
          // Escape in the rename field cancels the rename only: Radix hears it
          // on the document before the field can stop it.
          onEscapeKeyDown={(e) => {
            if (e.target instanceof HTMLInputElement) e.preventDefault()
          }}
        >
          <DrawerHeader>
            <DrawerTitle>Conversations</DrawerTitle>
          </DrawerHeader>
          <DrawerBody className="flex min-h-0 flex-col overflow-hidden p-0">
            <ConversationList onNavigate={() => setListOpen(false)} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      </div>
    </PageFrame>
  )
}
